import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { NavbarData } from '../App';
import { HeroHeadline } from './HeroHeadline';

import icon1 from '@/assets/icon1.png';
import icon2 from '@/assets/icon2.png';
import icon3 from '@/assets/icon3.png';
import icon4 from '@/assets/icon4.png';

export const Hero: React.FC<{
  navbarData: NavbarData
}> = ({ navbarData }) => {
  const { t } = useTranslation();

  return (
    <section id="home" className="relative isolate flex min-h-screen flex-col items-center justify-center overflow-hidden pt-20">
      {/* Background: img uses inset + min-height guard so desktop never collapses when parent height resolves oddly */}
      <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
        <img
          src={new URL('../assets/figma/hero_bg.png', import.meta.url).href}
          alt=""
          className="absolute inset-0 h-full min-h-[100dvh] w-full object-cover object-center"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black" aria-hidden />
      </div>

      <HeroHeadline />

      {/* Download buttons */}
      <div className="relative z-10 mx-auto flex w-full max-w-[min(100%,56rem)] flex-wrap justify-center gap-3 px-4 min-[475px]:gap-4 sm:gap-6 sm:px-6 mt-14">
        {
          navbarData.h5 ? (
            <a
              target='_blank'
              href={navbarData.h5}
              className="interactive-scale cursor-pointer interactive-surface flex max-w-[min(100%,20rem)] min-w-0 items-center gap-3 rounded-[10px] border-2 border-transparent bg-[#242424] px-[clamp(1rem,3vw,1.5rem)] py-3 text-left text-white sm:py-4 hover:border-[#BCAAFF] hover:bg-[radial-gradient(circle,#000_0%,#3A23A1_89%,#825DE0_94%,#D2C6EE_100%)] hover:shadow-[inset_0_1px_21px_rgba(255,255,255,0.5)] focus-visible:outline-none focus-visible:border-[#BCAAFF] focus-visible:bg-[radial-gradient(circle,#000_0%,#3A23A1_89%,#825DE0_94%,#D2C6EE_100%)] focus-visible:shadow-[inset_0_1px_21px_rgba(255,255,255,0.5)]"
            >
              <img style={{
                width: 30, height: 30
              }} src={icon1} alt="icon" />
              <span className="text-sm whitespace-pre-line leading-tight font-normal text-left sm:text-base">{t('hero-login-h5')}</span>
            </a>
          ) : null
        }{
          navbarData.android ? (
            <a
              target='_blank'
              href={navbarData.android}
              className="interactive-scale cursor-pointer interactive-surface flex max-w-[min(100%,20rem)] min-w-0 items-center gap-3 rounded-[10px] border-2 border-transparent bg-[#242424] px-[clamp(1rem,3vw,1.5rem)] py-3 text-left text-white sm:py-4 hover:border-[#BCAAFF] hover:bg-[radial-gradient(circle,#000_0%,#3A23A1_89%,#825DE0_94%,#D2C6EE_100%)] hover:shadow-[inset_0_1px_21px_rgba(255,255,255,0.5)] focus-visible:outline-none focus-visible:border-[#BCAAFF] focus-visible:bg-[radial-gradient(circle,#000_0%,#3A23A1_89%,#825DE0_94%,#D2C6EE_100%)] focus-visible:shadow-[inset_0_1px_21px_rgba(255,255,255,0.5)]"
            >
              <img style={{
                width: 30, height: 30
              }} src={icon2} alt="icon" />
              <span className="text-sm whitespace-pre-line leading-tight font-normal text-left sm:text-base">{t('hero-download-android')}</span>
            </a>
          ) : null
        }{
          navbarData.ios ? (
            <a
              target='_blank'
              href={navbarData.ios}
              className="interactive-scale cursor-pointer interactive-surface flex max-w-[min(100%,20rem)] min-w-0 items-center gap-3 rounded-[10px] border-2 border-transparent bg-[#242424] px-[clamp(1rem,3vw,1.5rem)] py-3 text-left text-white sm:py-4 hover:border-[#BCAAFF] hover:bg-[radial-gradient(circle,#000_0%,#3A23A1_89%,#825DE0_94%,#D2C6EE_100%)] hover:shadow-[inset_0_1px_21px_rgba(255,255,255,0.5)] focus-visible:outline-none focus-visible:border-[#BCAAFF] focus-visible:bg-[radial-gradient(circle,#000_0%,#3A23A1_89%,#825DE0_94%,#D2C6EE_100%)] focus-visible:shadow-[inset_0_1px_21px_rgba(255,255,255,0.5)]"
            >
              <img style={{
                width: 30, height: 30
              }} src={icon3} alt="icon" />
              <span className="text-sm whitespace-pre-line leading-tight font-normal text-left sm:text-base">{t('hero-download-ios')}</span>
            </a>
          ) : null
        }{
          navbarData.client ? (
            <a
              target='_blank'
              href={navbarData.client}
              className="interactive-scale cursor-pointer interactive-surface flex max-w-[min(100%,20rem)] min-w-0 items-center gap-3 rounded-[10px] border-2 border-transparent bg-[#242424] px-[clamp(1rem,3vw,1.5rem)] py-3 text-left text-white sm:py-4 hover:border-[#BCAAFF] hover:bg-[radial-gradient(circle,#000_0%,#3A23A1_89%,#825DE0_94%,#D2C6EE_100%)] hover:shadow-[inset_0_1px_21px_rgba(255,255,255,0.5)] focus-visible:outline-none focus-visible:border-[#BCAAFF] focus-visible:bg-[radial-gradient(circle,#000_0%,#3A23A1_89%,#825DE0_94%,#D2C6EE_100%)] focus-visible:shadow-[inset_0_1px_21px_rgba(255,255,255,0.5)]"
            >
              <img style={{
                width: 30, height: 30
              }} src={icon4} alt="icon" />
              <span className="text-sm whitespace-pre-line leading-tight font-normal text-left sm:text-base">{t('hero-download-windows')}</span>
            </a>
          ) : null
        }
      </div>
    </section>
  );
};
